import { ReactNode, useId, useRef } from "react";
import { FormControlLabel, Link, List, ListItem, ListProps, Popover, Typography } from "@mui/material";
import { CoreButton } from "../button/Button";
import { CoreCheckbox } from "../checkbox/Checkbox";
import styles from "./index.module.css";
import i18nPackage from "../../i18n/i18n";
import CoreFocusTrap from "../focus/FocusTrap.tsx";

type FilterSectionTitleAlignment = "center" | "top";

export function CoreFilterSection({
	title,
	className,
	children,
	alignment = "center",
}: {
	title: ReactNode;
	className?: string;
	children: ReactNode;
	alignment?: FilterSectionTitleAlignment;
}) {
	return (
		<tr className={`${styles.filterSection} ${className || ""}`}>
			<th className={alignment === "top" ? styles.topFilterSectionCell : undefined}>
				<Typography className={styles.filterSectionHeader}>{title}</Typography>
			</th>
			<td>{children}</td>
		</tr>
	);
}

type CoreFilterCheckboxEntryProps = {
	checked?: boolean;
	onChange: (newValue: boolean) => void;
	className?: string;
	children: ReactNode;
};

export function CoreFilterCheckboxEntry({ checked, onChange, className, children }: CoreFilterCheckboxEntryProps) {
	return (
		<ListItem disablePadding disableGutters>
			<FormControlLabel
				className={`${styles.checkboxEntry} ${className || ""}`}
				control={
					<CoreCheckbox
						checked={!!checked}
						indeterminate={checked == null}
						onChange={(e) => onChange(e.target.checked)}
					/>
				}
				label={children}
			/>
		</ListItem>
	);
}

export type CoreFilterSectionOption = {
	sectionTitle: string;
	alignment?: FilterSectionTitleAlignment;
} & ({ checkboxes: CoreFilterCheckboxEntryProps[] } | { customFilter: () => ReactNode });

export function CoreFilterList({ className, children, ...listProps }: ListProps) {
	return (
		<List {...listProps} className={`${styles.verticalCheckboxList} ${className || ""}`}>
			{children}
		</List>
	);
}

export type CoreFilterPopoverProps = {
	filterCount?: number;
	onClear: () => void;
	anchorEl: HTMLButtonElement | null;
	onClose: () => void;
	onApply: () => void;
	children: ReactNode;
	id: string;
	inShadowDom?: boolean;
};

export default function CoreFilterPopover({
	filterCount = 0,
	onClear,
	anchorEl,
	onClose,
	onApply,
	children,
	id,
	inShadowDom,
}: CoreFilterPopoverProps) {
	const popoverTitleID = useId();
	const firstFocusable = useRef<HTMLButtonElement | null>(null);
	const lastFocusable = useRef<HTMLButtonElement | null>(null);

	return (
		<Popover
			open
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
			slotProps={{
				paper: {
					className: styles.filterPopoverContent,
					id: id,
					"aria-labelledby": popoverTitleID,
					role: "dialog",
					"aria-modal": "true",
				},
			}}
		>
			{/**Focus does not get trapped in popover ONLY when in shadow dom, so the code below patches the issue. */}
			{inShadowDom && <CoreFocusTrap onFocus={() => firstFocusable.current?.focus()} />}
			{inShadowDom && <CoreFocusTrap onFocus={() => lastFocusable.current?.focus()} />}
			<div className={styles.filterHeader}>
				<Typography variant="h2" className={styles.filterTitle} id={popoverTitleID}>
					{i18nPackage.t("coreComponents.filter.title")}
					{filterCount ? ` (${filterCount})` : null}
				</Typography>
				<Link component="button" className={styles.link} onClick={onClear} ref={firstFocusable}>
					{i18nPackage.t("coreComponents.filter.clear")}
				</Link>
			</div>
			<div className={styles.filterBody}>
				<table className={styles.filterList}>
					<tbody>{children}</tbody>
				</table>
			</div>
			<div className={styles.filterButtons}>
				<CoreButton color="secondary" onClick={onClose}>
					{i18nPackage.t("coreComponents.filter.cancel")}
				</CoreButton>
				<CoreButton
					onClick={() => {
						onApply();
						onClose();
					}}
					ref={lastFocusable}
				>
					{i18nPackage.t("coreComponents.filter.apply")}
				</CoreButton>
			</div>
			{inShadowDom && <CoreFocusTrap onFocus={() => firstFocusable.current?.focus()} />}
		</Popover>
	);
}
